import React, { useMemo } from 'react'
import Select, { ActionMeta } from 'react-select'
import { t } from 'src/translations'

import { Modifier } from 'src/apollo/onlineOrdering'
import FormattedPrice from 'shared/components/common/price/FormattedPrice'

import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { ModifierGroup } from 'public/components/online_ordering/types'
import SelectionList from 'public/components/default_template/online_ordering/item_modal/SelectionList'
import { requiresSelectionList } from './requiresSelectionList'
import { ModifierGroupWrapper } from './ModifierGroupWrapper'
import { PDP_MODIFIER_INLINE_OPTION_LIMIT } from './pdpModifierUiConstants'

export const ItemModifiersSection = ({
  groups
}: {
  groups: ModifierGroup[]
}) => {
  const {
    registerModifierGroup,
    isInvalid: isModifierInvalid,
    isValid: isModifierValid
  } = useModifierContext()
  // Otherwise, render the standard modifier groups
  return (
    <div className='ecommPdpModifiers' data-testid='modifierGroups'>
      {groups?.map((group: ModifierGroup, index) => {
        const { guid, name, minSelections, maxSelections } = group
        const required = Boolean(minSelections && minSelections > 0)
        const ariaLabel = `${name}-label`

        // Use SelectionList for complex groups (nested modifiers, multi-select, or duplicates)
        if (requiresSelectionList(group)) {
          return (
            <SelectionList
              key={guid}
              group={group}
              firstModifierGroup={index === 0}
              inlineOptionLimit={PDP_MODIFIER_INLINE_OPTION_LIMIT}
              variant='pdp'
            />
          )
        }

        // Use dropdown selector for simple single-select flat groups
        return (
          <ModifierGroupWrapper
            key={guid}
            guid={guid}
            name={name}
            ariaLabel={ariaLabel}
            required={required}
            minSelections={minSelections}
            maxSelections={maxSelections}
            isModifierInvalid={isModifierInvalid}
            isModifierValid={isModifierValid}
            registerModifierGroup={registerModifierGroup}
          >
            <ModifierSelect group={group} ariaLabel={ariaLabel} />
          </ModifierGroupWrapper>
        )
      })}
    </div>
  )
}

type OptionType = {
  value: Modifier
  label: React.ReactNode
}

const ModifierSelect = ({
  group,
  ariaLabel
}: {
  group: ModifierGroup
  ariaLabel: string
}) => {
  const {
    displayedModifier,
    select,
    deselect,
    modifierGroupData,
    isInvalid,
    getGroupSelectionsCount
  } = useModifierContext()
  const { guid, modifiers, minSelections, maxSelections } = group
  const required = Boolean(minSelections && minSelections > 0)

  const ariaAlertText = useMemo(() => {
    if (group) {
      const { guid, name, minSelections, maxSelections } = group
      if (isInvalid(guid)) {
        const currentSelectionsCount = getGroupSelectionsCount(guid)
        if (minSelections && maxSelections) {
          return t('modifiers-section.aria-select-between', {
            min: minSelections,
            max: maxSelections,
            name,
            count: currentSelectionsCount
          })
        } else if (minSelections && currentSelectionsCount) {
          return t('modifiers-section.aria-select-at-least', {
            min: minSelections,
            name,
            count: currentSelectionsCount
          })
        }
        return t('modifiers-section.aria-make-selection', { name })
      }
    }
    return ''
  }, [getGroupSelectionsCount, group, isInvalid])

  // eslint-disable-next-line no-extra-parens
  const options: OptionType[] = (modifiers as Modifier[]).map((modifier) => {
    return {
      value: modifier,
      label: modifier.name || ''
    }
  })
  const selectedValues = options.filter((option) => {
    const selectedItem =
      displayedModifier?.modifierGroups[group.guid]?.[option.value.itemGuid]
    return !!selectedItem
  })

  const formatOptionLabel = ({ value: modifier }: OptionType) => {
    const selectedItem =
      displayedModifier?.modifierGroups[guid]?.[modifier.itemGuid]

    const isIncludedDefault =
      modifier.isDefault &&
      modifierGroupData[guid]?.defaultOptionsChargePrice === 'NO'
    const shouldShowPrice =
      modifier.price &&
      modifier.price !== 0 &&
      (!isIncludedDefault || (selectedItem && selectedItem.quantity > 1))
    const quantity = selectedItem?.quantity || 1

    return (
      <div className='modifierTextContent' key={modifier.itemGuid}>
        <div className='modifierText'>
          <div>{modifier.name}</div>
        </div>
        {modifier.price && shouldShowPrice ? (
          <span className={'price'}>
            {modifier.price > 0 ? '+' : '-'}
            <FormattedPrice
              value={
                (isIncludedDefault ? quantity - 1 : quantity) *
                Math.abs(modifier.price)
              }
            />
          </span>
        ) : null}
      </div>
    )
  }

  const onChange = (
    selections: OptionType | OptionType[],
    actionMeta: ActionMeta<OptionType>
  ) => {
    switch (actionMeta.action) {
      case 'select-option': {
        const modifier: Modifier | null | undefined = !Array.isArray(selections)
          ? selections?.value
          : !Array.isArray(actionMeta.option)
          ? actionMeta.option?.value
          : null
        if (modifier) {
          select(
            guid,
            modifier.itemGuid,
            1,
            actionMeta.option ? 'merge' : 'replace',
            modifier.itemGroupGuid,
            modifier.name || undefined,
            modifier.price,
            modifier.isDefault
          )
        }
        break
      }
      case 'pop-value':
      case 'remove-value': {
        deselect(guid, actionMeta.removedValue.value.itemGuid)
        break
      }
      case 'clear': {
        const modifiersToClear = actionMeta.removedValues
        modifiersToClear.forEach((modifier) =>
          deselect(guid, modifier.value.itemGuid)
        )
        break
      }
    }
  }

  return (
    <Select
      inputId={guid}
      aria-labelledby={ariaLabel}
      instanceId={guid}
      classNamePrefix='react-select'
      ariaLiveMessages={{ guidance: () => ariaAlertText }}
      isClearable={!required}
      isSearchable={false}
      isMulti={maxSelections !== 1}
      required={required}
      menuPosition='fixed'
      value={selectedValues}
      options={options}
      formatOptionLabel={formatOptionLabel}
      onChange={onChange}
    />
  )
}
